import styled from 'styled-components'
import { useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'

import { VideoData } from '../../../interfaces/VideoOrStream'
import { useAtom } from 'jotai'
import { animated } from 'react-spring'
import VideoElement from './VideoElement'
import { frameRefAtom, fullscreenAtom } from './atoms'
import WhiteFlash from './WhiteFlash'
import CardOverlay from './CardOverlay'
import { useScheduleTime } from '../../../hooks/droptime'

const CustomOverlay = dynamic(() => import('./CustomOverlay'), { ssr: false })

//* ============================= Types ========================================

type VideoPlayerProps = {
  videoMetadata: VideoData
  cardMode: boolean
  onCardClick?: () => void
}

//* ======================== React component ===================================

/**
 * The video player component, which is meant to be used on the video page.
 * The player consists of a default HTML5 video element and custom overlays.
 */
const VideoPlayer = ({ videoMetadata, cardMode, onCardClick }: VideoPlayerProps): JSX.Element => {
  const frameRef = useRef<HTMLDivElement>(null)
  const [, setFrameRef] = useAtom(frameRefAtom)
  // const [sticky, setSticky] = useState(false)
  const { scheduleTime = new Date() } = useScheduleTime(videoMetadata.address)

  //TODO: Find a different way for doing this, cause it breaks the abstraction.
  const [isFullscreen] = useAtom(fullscreenAtom)

  useEffect(() => {
    setFrameRef(frameRef.current)
  }, [setFrameRef])

  return (
    <Frame ref={frameRef} isFullscreenMobile={isFullscreen} cardMode={cardMode} sticky={false}>
      <WhiteFlash />
      <VideoElement
        videoMetadata={videoMetadata}
        scheduledTime={scheduleTime}
        startMuted={cardMode}
        loop={cardMode}
      />
      {cardMode ? (
        <CardOverlay video={videoMetadata} scheduledTime={scheduleTime} onClick={onCardClick!} />
      ) : (
        <CustomOverlay videoMetadata={videoMetadata} scheduledTime={scheduleTime} />
      )}
    </Frame>
  )
}

export default VideoPlayer

//* ======================== Styled components =================================

/**
 * Just a container for the components which make the video player (e.g. overlay, player etc.).
 */
const Frame = styled(animated.div)<{
  isFullscreenMobile: boolean
  cardMode: boolean
  sticky: boolean
}>`
  position: ${(props) => (props.cardMode || !props.sticky ? 'relative' : 'fixed')};
  margin-top: ${(props) => (props.cardMode || !props.sticky ? '0' : '-25px')};
  padding-bottom: ${(props) => {
    // When fullscreen is enabled, deactivate this padding, as it causes the video to be cut off.
    if (props.isFullscreenMobile) {
      return '0'
    }
    return '56.25%'
  }};
  width: 100%;
  z-index: 10;
  background-color: black;
  border-radius: ${(props) => (props.cardMode ? '10px' : '0')};
  overflow: ${(props) => (props.cardMode ? 'hidden' : 'visible')};

  @media (min-width: 43em) {
    margin-top: 0;
    margin-bottom: 0;
    position: relative;
    border-radius: 15px;
    overflow: hidden;
  }
`
